import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError, api } from '../../api';
import { useAuth } from '../../auth-context';
import { t } from '../../i18n';
import type { ChildPublic } from '../../types';
import { AppShell } from '../shared/AppShell';
import { Avatar } from '../shared/Avatar';
import { PinKeypad } from '../shared/PinKeypad';

type Props = { mode: 'child' | 'parent' };

export function PinEntry({ mode }: Props) {
  const navigate = useNavigate();
  const params = useParams<{ id?: string }>();
  const auth = useAuth();
  const [child, setChild] = useState<ChildPublic | null>(null);
  const [shake, setShake] = useState(0);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [lockedSeconds, setLockedSeconds] = useState(0);

  useEffect(() => {
    if (mode !== 'child' || !params.id) return;
    api.childrenList().then((res) => {
      const found = res.children.find((c) => c.id === Number(params.id)) ?? null;
      setChild(found);
    });
  }, [mode, params.id]);

  useEffect(() => {
    if (lockedSeconds <= 0) return;
    const t = setInterval(() => setLockedSeconds((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [lockedSeconds]);

  const onComplete = async (pin: string) => {
    setErrorText(null);
    try {
      if (mode === 'child') {
        const id = Number(params.id);
        await auth.loginChild(id, pin);
        navigate('/child', { replace: true });
      } else {
        await auth.loginParent(pin);
        navigate('/parent', { replace: true });
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setLockedSeconds(err.retry_after_seconds ?? 900);
        setErrorText(t('pin.error.locked'));
      } else {
        setErrorText(t('pin.error.wrong'));
        setShake((n) => n + 1);
      }
    }
  };

  const title = mode === 'child' ? t('pin.child.title') : t('pin.parent.title');

  return (
    <AppShell ramp={child?.color_ramp}>
      <main className="mx-auto flex min-h-dvh max-w-md flex-col p-6">
        <button
          onClick={() => navigate('/')}
          aria-label={t('common.back')}
          className="self-start text-ink-muted"
        >
          <ArrowLeft size={22} />
        </button>
        <div className="flex flex-1 flex-col items-center justify-center gap-6">
          {child ? (
            <div className="flex flex-col items-center gap-2">
              <Avatar
                initial={child.avatar_initial}
                ramp={child.color_ramp}
                size="lg"
                src={child.avatar_url ?? undefined}
              />
              <div className="text-[18px] font-medium">{child.name}</div>
            </div>
          ) : null}
          <p className="text-center text-[14px] text-ink-muted">{title}</p>
          <PinKeypad
            length={mode === 'child' ? 4 : 6}
            disabled={lockedSeconds > 0}
            shake={shake > 0 && shake % 2 === 1}
            onComplete={onComplete}
          />
          {errorText ? (
            <p className="text-[12px] text-danger">
              {errorText}
              {lockedSeconds > 0 ? ` · ${lockedSeconds}s` : ''}
            </p>
          ) : null}
        </div>
      </main>
    </AppShell>
  );
}
